import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import {  UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { GreenCementTest, PreInstallLoadType } from 'src/app/perivis/models/load-case.model';

export interface GreenCementTestForm {
  type: PreInstallLoadType;
  PumpPressure: number;
  HasPlug: boolean;
  PlugDepth: number;
}

@Component({
    selector: 'app-green-cement-test-details',
    template: `
    <form [formGroup]="form.formGroup">
      <div class="flex flex-col pt-2">
          <label>Pump Pressure</label>
          <app-validated-number-input
            [id]="'PumpPressure'"
            [control]="control('PumpPressure')"
            [value]="control('PumpPressure').value"
            [validation]="pressureValidation"
            [unit]="pressureUnit"
            [inline]="false">
          </app-validated-number-input>
        </div>

        @if (!isCemented) {
          <div class="flex items-center pt-4 pb-2">
            <p-checkbox
              inputId="plug"
              size="small"
              binary="true"
              formControlName="HasPlug"
              (change)="hasPlugChange()"
            ></p-checkbox>
            <label for="plug" class="ml-2 pt-1">Has Plug</label>
          </div>

          <div class="flex flex-col">
            <label>Plug Depth</label>
            <app-validated-number-input
              [id]="'PlugDepth'"
              [control]="control('PlugDepth')"
              [value]="control('PlugDepth').value"
              [validation]="depthValidation"
              [unit]="depthUnit + ' MD'"
              [inline]="false"
              [readonly]="!control('HasPlug').value">
            </app-validated-number-input>
          </div>
        }
    </form>
  `,
    providers: subformComponentProviders(GreenCementTestDetailsComponent),
    standalone: false
})
export class GreenCementTestDetailsComponent implements OnInit, AfterViewInit {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }

  @Input() userUnits: UserUnitsModel;
  @Input() hangerMd: number;
  @Input() shoeMd: number;
  @Input() isCemented: boolean;

  public pressureUnit: string;
  public depthUnit: string;
  public pressureValidation: { min: number, max: number };
  public depthValidation: { min: number, max: number };

  ngOnInit(): void {
    this.pressureUnit = unitsLib[this.userUnits.pressure].symbol;
    this.depthUnit = this.userUnits.longLengths;
    
    switch (this.userUnits.pressure) {
      case 'psi':
        this.pressureValidation = { min: 0, max: 30000 };
        break;
      case 'bar':
        this.pressureValidation = { min: 0, max: 2068 };
        break;
      case 'KPa':
        this.pressureValidation = { min: 0, max: 206800 };
        break;
      case 'atm':
        this.pressureValidation = { min: 0, max: 2041 };
        break;
    }
    this.depthValidation = { min: this.hangerMd, max: this.shoeMd };
    this.form.formGroup.controls.PumpPressure.setValidators([Validators.min(this.pressureValidation.min), Validators.max(this.pressureValidation.max), Validators.required]);
    this.form.formGroup.controls.PlugDepth.setValidators([Validators.min(this.hangerMd), Validators.max(this.shoeMd)]);
  }

  ngAfterViewInit(): void {
    if (this.form.formGroup.controls.HasPlug.value) {
      this.hasPlug();
    } else {
      this.doesntHavePlug();
    }
  }

  public form = createForm<GreenCementTestForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(null),
      PumpPressure: new UntypedFormControl(null, { updateOn: 'blur' }),
      HasPlug: new UntypedFormControl(false),
      PlugDepth: new UntypedFormControl(null, { updateOn: 'blur' }),
    },
    toFormGroup: (obj: GreenCementTest | null): GreenCementTestForm => {
      if (!obj) {
        return null;
      }

      return {
        type: obj.type,
        PumpPressure: obj.PumpPressure,
        HasPlug: obj.HasPlug,
        PlugDepth: obj.PlugDepth
      }
    },
    fromFormGroup: (formValue: GreenCementTestForm): GreenCementTest => {
      return {
        type: PreInstallLoadType.GREENCEMENTTEST,
        PumpPressure: formValue.PumpPressure,
        HasPlug: formValue.HasPlug,
        PlugDepth: formValue.PlugDepth ? formValue.PlugDepth : this.shoeMd,
      }
    }
  })

    public hasPlugChange() {
    if (this.form.formGroup.controls.HasPlug.value) {
      this.hasPlug();
    } else {
      this.doesntHavePlug();
    }
  }

  private hasPlug() {
    if (!this.form.formGroup.controls.PlugDepth.value) {
      this.form.formGroup.controls.PlugDepth.setValue(this.shoeMd, { emitEvent: false });
    }
  }

  private doesntHavePlug() {
    this.form.formGroup.controls.PlugDepth.disable({ emitEvent: false });
  }
}
